import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_URL = process.env.PUBLIC_URL || '/';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
}

// Response interceptor to handle token refresh with synchronization
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthRequest = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        isRefreshing = false;
        onRefreshed(access_token);

        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        const loginPath = `${PUBLIC_URL.replace(/\/$/, '')}/login`;
        window.location.href = loginPath;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Super Admin API
export const superAdminAPI = {
  getCompanies: (params) => api.get('/superadmin/companies', { params }),
  getCompaniesCount: (params) => api.get('/superadmin/companies/count', { params }),
  getCompany: (id) => api.get(`/superadmin/companies/${id}`),
  createCompany: (data) => api.post('/superadmin/companies', data),
  updateCompany: (id, data) => api.put(`/superadmin/companies/${id}`, data),
  updateCompanyModules: (id, modules) => api.patch(`/superadmin/companies/${id}/modules`, modules),
  deactivateCompany: (id) => api.delete(`/superadmin/companies/${id}`),
  reactivateCompany: (id) => api.post(`/superadmin/companies/${id}/reactivate`),
  purgeCompany: (id) => api.delete(`/superadmin/companies/${id}/purge`),
  getCompanyAnalytics: (id, params) => api.get(`/superadmin/companies/${id}/analytics`, { params }),
  getDashboardStats: () => api.get('/superadmin/dashboard/stats'),
  exportCompanyData: (id) => api.get(`/superadmin/companies/${id}/export`, { responseType: 'blob' }),
};

// Company API
export const companyAPI = {
  getCurrent: () => api.get('/companies/current'),
  updateCurrent: (data) => api.put('/companies/current', data),
  uploadLogo: (formData) => api.post('/companies/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getDashboardStats: () => api.get('/companies/dashboard/stats'),
};

// Employee API
export const employeeAPI = {
  getEmployees: (params) => api.get('/employees', { params }),
  getEmployee: (id) => api.get(`/employees/${id}`),
  createEmployee: (data) => api.post('/employees', data),
  updateEmployee: (id, data) => api.put(`/employees/${id}`, data),
  deactivateEmployee: (id) => api.delete(`/employees/${id}`),
  reactivateEmployee: (id) => api.put(`/employees/${id}/reactivate`),
  bulkCreateEmployees: (data) => api.post('/employees/bulk', data),
  getDirectory: (params) => api.get('/employees/directory', { params }),
  getDepartments: () => api.get('/employees/departments'),
  getCelebrations: () => api.get('/employees/celebrations'),
  isManager: () => api.get('/employees/is-manager'),
  uploadProfilePicture: (id, formData) => api.post(`/employees/${id}/profile-picture`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  resetPassword: (id, data) => api.put(`/employees/${id}/password`, data),
  downloadAppointmentLetter: (id) => api.get(`/employees/${id}/appointment-letter`, { responseType: 'blob' }),
};

// Leave API
export const leaveAPI = {
  getTypes: (params) => api.get('/leaves/types', { params }),
  createType: (data) => api.post('/leaves/types', data),
  updateType: (id, data) => api.put(`/leaves/types/${id}`, data),
  deleteType: (id) => api.delete(`/leaves/types/${id}`),
  getRequests: (params) => api.get('/leaves/requests', { params }),
  getPendingRequests: () => api.get('/leaves/requests/pending'),
  createRequest: (data) => api.post('/leaves/requests', data),
  approveRequest: (id, remarks) => api.put(`/leaves/requests/${id}/approve`, null, { params: { remarks } }),
  rejectRequest: (id, remarks) => api.put(`/leaves/requests/${id}/reject`, null, { params: { remarks } }),
  cancelRequest: (id) => api.put(`/leaves/requests/${id}/cancel`),
  getBalance: (employeeId, year) => api.get(`/leaves/balance/${employeeId}`, { params: { year } }),
  getHolidays: (year) => api.get('/leaves/holidays', { params: { year } }),
  createHoliday: (data) => api.post('/leaves/holidays', data),
  updateHoliday: (id, data) => api.put(`/leaves/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/leaves/holidays/${id}`),
  uploadHolidays: (formData) => api.post('/leaves/holidays/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getHolidayRequests: (params) => api.get('/leaves/holidays/requests', { params }),
  getPendingHolidayRequests: () => api.get('/leaves/holidays/requests/pending'),
  createHolidayRequest: (data) => api.post('/leaves/holidays/requests', data),
  approveHolidayRequest: (id) => api.put(`/leaves/holidays/requests/${id}/approve`),
  rejectHolidayRequest: (id) => api.put(`/leaves/holidays/requests/${id}/reject`),
  cancelHolidayRequest: (id) => api.put(`/leaves/holidays/requests/${id}/cancel`),
};

// Salary Structure API
export const salaryAPI = {
  getStructures: (params) => api.get('/salary-structures', { params }),
  getStructure: (id) => api.get(`/salary-structures/${id}`),
  getEmployeeStructure: (employeeId) => api.get(`/salary-structures/employee/${employeeId}`),
  getEmployeeHistory: (employeeId) => api.get(`/salary-structures/employee/${employeeId}/history`),
  createStructure: (data) => api.post('/salary-structures', data),
  updateStructure: (id, data) => api.put(`/salary-structures/${id}`, data),
  deleteStructure: (id) => api.delete(`/salary-structures/${id}`),
};

// Payroll API
export const payrollAPI = {
  getRuns: (params) => api.get('/payroll/runs', { params }),
  getRun: (id) => api.get(`/payroll/runs/${id}`),
  createRun: (data) => api.post('/payroll/runs', data),
  processRun: (id) => api.post(`/payroll/runs/${id}/process`),
  lockRun: (id) => api.post(`/payroll/runs/${id}/lock`),
  getRunEmployees: (runId) => api.get(`/payroll/runs/${runId}/employees`),
  getPayslip: (employeeId, runId) => api.get(`/payroll/payslip/${employeeId}/${runId}`),
  downloadBankLetter: (runId) => api.get(`/payroll/runs/${runId}/bank-letter`, { responseType: 'blob' }),
  downloadPayslipPdf: (employeeId, runId) =>
    api.get(`/payroll/payslip/${employeeId}/${runId}/pdf`, { responseType: 'blob' }),
  getMyPayslips: () => api.get('/payroll/my-payslips'),
  getTDSProjection: (employeeId) => api.get(`/payroll/projection/${employeeId}`),
  calculateArrears: (data) => api.post('/payroll/arrears/calculate', data),
  applyArrears: (runId, data) => api.post(`/payroll/runs/${runId}/arrears`, data),
  addAdhoc: (runId, empId, data) => api.post(`/payroll/runs/${runId}/employees/${empId}/adhoc`, data),
  removeAdhoc: (runId, empId, code) => api.delete(`/payroll/runs/${runId}/employees/${empId}/adhoc/${code}`),
  setTaxOverride: (runId, empId, data) => api.put(`/payroll/runs/${runId}/employees/${empId}/tax-override`, data),
  deleteRun: (id) => api.delete(`/payroll/runs/${id}`),
};

export const advanceAPI = {
  getAll: (params) => api.get('/salary-advances', { params }),
  request: (data) => api.post('/salary-advances', data),
  grant: (empId, data) => api.post(`/salary-advances/grant?employee_id=${empId}`, data),
  update: (id, data) => api.put(`/salary-advances/${id}`, data),
  updateStatus: (id, data) => api.put(`/salary-advances/${id}/status`, data),
  delete: (id) => api.delete(`/salary-advances/${id}`),
  getRepayments: (id) => api.get(`/salary-advances/${id}/repayments`),
  getHistory: (id) => api.get(`/salary-advances/${id}/history`),
};

export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
};

// Performance API
export const performanceAPI = {
  getCycles: (params) => api.get('/performance/cycles', { params }),
  getCycle: (id) => api.get(`/performance/cycles/${id}`),
  createCycle: (data) => api.post('/performance/cycles', data),
  updateCycle: (id, data) => api.put(`/performance/cycles/${id}`, data),
  getGoals: (params) => api.get('/performance/goals', { params }),
  createGoal: (data) => api.post('/performance/goals', data),
  updateGoal: (id, data) => api.put(`/performance/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`/performance/goals/${id}`),
  getReviews: (params) => api.get('/performance/reviews', { params }),
  getReview: (id) => api.get(`/performance/reviews/${id}`),
  createReview: (data) => api.post('/performance/reviews', data),
  submitSelfReview: (id, data) => api.put(`/performance/reviews/${id}/self`, data),
  submitManagerReview: (id, data) => api.put(`/performance/reviews/${id}/manager`, data),
  getPendingManagerReviews: () => api.get('/performance/reviews/pending/manager'),
};

// Tax API
export const taxAPI = {
  getDeclaration: (financialYear) => api.get('/tax/declaration', { params: { financial_year: financialYear } }),
  submitDeclaration: (data) => api.post('/tax/declaration', data),
  getSettings: () => api.get('/tax/settings'),
  updateSettings: (data) => api.post('/tax/settings', data),
  getDeclarations: (financialYear) => api.get('/tax/declarations', { params: { financial_year: financialYear } }),
  verifyDeclaration: (id, data) => api.post(`/tax/declarations/${id}/verify`, data),
  getComparison: (financialYear) => api.get('/tax/comparison', { params: { financial_year: financialYear } }),
  downloadForm16: (financialYear, employeeId) =>
    api.get('/tax/form16/download', {
      params: { financial_year: financialYear, employee_id: employeeId },
      responseType: 'blob'
    }),
};

// Settings API
export const settingsAPI = {
  getDepartments: () => api.get('/settings/departments'),
  createDepartment: (data) => api.post('/settings/departments', data),
  updateDepartment: (id, data) => api.put(`/settings/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/settings/departments/${id}`),
  getDesignations: () => api.get('/settings/designations'),
  createDesignation: (data) => api.post('/settings/designations', data),
  updateDesignation: (id, data) => api.put(`/settings/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/settings/designations/${id}`),
  // Asset Categories
  getAssetCategories: () => api.get('/settings/asset-categories'),
  createAssetCategory: (data) => api.post('/settings/asset-categories', data),
  updateAssetCategory: (id, data) => api.put(`/settings/asset-categories/${id}`, data),
  deleteAssetCategory: (id) => api.delete(`/settings/asset-categories/${id}`),
  // Locations
  getLocations: () => api.get('/settings/locations'),
  createLocation: (data) => api.post('/settings/locations', data),
  updateLocation: (id, data) => api.put(`/settings/locations/${id}`, data),
  deleteLocation: (id) => api.delete(`/settings/locations/${id}`),
  resolveCoords: (url) => api.get('/settings/resolve-coords', { params: { url } }),
  // Company Preferences
  getPreferences: () => api.get('/settings/preferences'),
  updatePreferences: (data) => api.put('/settings/preferences', data),
  testCelebrationEmail: () => api.post('/settings/test-celebration-email'),
  testAbsentEmail: () => api.post('/settings/test-absent-email'),
};

// Professional Tax Configs API
export const ptAPI = {
  getConfigs: (params) => api.get('/settings/pt-configs', { params }),
  createConfig: (data) => api.post('/settings/pt-configs', data),
  updateConfig: (id, data) => api.put(`/settings/pt-configs/${id}`, data),
  deleteConfig: (id) => api.delete(`/settings/pt-configs/${id}`),
};

export const onboardingAPI = {
  getCompanyInfo: (companyId) => api.get(`/onboarding/company/${companyId}`),
  submit: (data) => api.post('/onboarding/submit', data),
  getRequests: (params) => api.get('/onboarding/requests', { params }),
  approve: (id, remarks, employeeData) => api.post(`/onboarding/approve/${id}`, { admin_remarks: remarks, employee_data: employeeData }),
  reject: (id, remarks) => api.post(`/onboarding/reject/${id}`, null, { params: { admin_remarks: remarks } }),
  uploadProfilePicture: (formData) => api.post('/onboarding/upload-profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const feedAPI = {
  getFeeds: () => api.get('/feeds'),
  createPost: (data) => api.post('/feeds', data),
  updatePost: (id, data) => api.put(`/feeds/${id}`, data),
  deletePost: (id) => api.delete(`/feeds/${id}`),
  generateAiPost: (prompt) => api.post('/feeds/generate-ai-post', { prompt }),
  toggleLike: (id) => api.post(`/feeds/${id}/like`),
  addComment: (id, content) => api.post(`/feeds/${id}/comment`, { content }),
  deleteComment: (postId, commentId) => api.delete(`/feeds/${postId}/comment/${commentId}`),
};

export const systemAPI = {
  getSettings: () => api.get(`/system/settings?t=${new Date().getTime()}`),
  updateSettings: (data) => api.post('/system/settings', data),
};

export const attendanceAPI = {
  getStatus: () => api.get('/attendance/status'),
  checkIn: (data) => api.post('/attendance/check-in', data),
  checkOut: (data) => api.post('/attendance/check-out', data),
  getHistory: (params) => api.get('/attendance/history', { params }),
  regularize: (data) => api.post('/attendance/regularize', data),
  getRequests: () => api.get('/attendance/admin/requests'),
  approveRegularization: (id, data) => api.post(`/attendance/admin/requests/${id}/approve`, data),
  bulkActionRegularization: (data) => api.post('/attendance/admin/requests/bulk-action', data),
  getManagerRequests: () => api.get('/attendance/manager/requests'),
  managerApproveRegularization: (id, data) => api.post(`/attendance/manager/requests/${id}/approve`, data),
  getConfig: (employeeId) => api.get('/attendance/config', { params: { employee_id: employeeId } }),
  updateConfig: (data) => api.post('/attendance/config', data),
  deleteConfig: (employeeId) => api.delete('/attendance/config', { params: { employee_id: employeeId } }),
  getMyConfig: () => api.get('/attendance/config/my'),
};

export const workScheduleAPI = {
  getAll: () => api.get('/work-schedules/'),
  create: (data) => api.post('/work-schedules/', data),
  update: (id, data) => api.put(`/work-schedules/${id}`, data),
  delete: (id) => api.delete(`/work-schedules/${id}`),
};

// RBAC API
export const rbacAPI = {
  getRoles: (companyId) => api.get('/rbac/roles', { params: { company_id: companyId } }),
  getRole: (id) => api.get(`/rbac/roles/${id}`),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (id, data) => api.put(`/rbac/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/rbac/roles/${id}`),
  getUsers: (companyId) => api.get('/rbac/users', { params: { company_id: companyId } }),
  createUser: (data) => api.post('/rbac/users', data),
  updateUser: (id, data) => api.put(`/rbac/users/${id}`, data),
  deleteUser: (id) => api.delete(`/rbac/users/${id}`),
  getModules: (companyId) => api.get(`/rbac/modules/${companyId}`),
};

// Asset Management API
export const assetAPI = {
  getAssets: (params) => api.get('/assets', { params }),
  getAsset: (id) => api.get(`/assets/${id}`),
  createAsset: (data) => api.post('/assets', data),
  updateAsset: (id, data) => api.put(`/assets/${id}`, data),
  deleteAsset: (id) => api.delete(`/assets/${id}`),
  assignAsset: (id, data) => api.post(`/assets/${id}/assign`, data),
  unassignAsset: (id, notes) => api.post(`/assets/${id}/unassign`, {}, notes ? { params: { notes } } : {}),
  getHistory: (id) => api.get(`/assets/${id}/history`),
  addMaintenance: (id, data) => api.post(`/assets/${id}/maintenance`, data),
  getMaintenanceHistory: (id) => api.get(`/assets/${id}/maintenance`),
  getRequests: (params) => api.get('/assets/requests', { params }),
  createRequest: (data) => api.post('/assets/requests', data),
  approveRequest: (id, fulfilledAssetId) => api.put(`/assets/requests/${id}/approve`, null, { params: { fulfilled_asset_id: fulfilledAssetId } }),
  rejectRequest: (id, reason) => api.put(`/assets/requests/${id}/reject`, null, { params: { reason } }),
  bulkUpload: (formData) => api.post('/assets/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getStats: () => api.get('/assets/stats'),
  getEmployeeAssets: (employeeId) => api.get(`/assets/employee/${employeeId}`),
};

// Bonus API
export const bonusAPI = {
  getBonuses: (financialYear) => api.get('/bonus', { params: { financial_year: financialYear } }),
  calculate: (financialYear) => api.post('/bonus/calculate', null, { params: { financial_year: financialYear } }),
  markPaid: (id) => api.put(`/bonus/${id}/pay`),
};

// FnF API
export const fnfAPI = {
  calculate: (id, params) => api.post('/fnf/calculate', { employee_id: id, ...params }),
  finalize: (id, data) => api.post('/fnf/finalize', { employee_id: id, ...data }),
  getAll: (params) => api.get('/fnf/all', { params }),
  downloadPdf: (id) => api.get(`/fnf/${id}/pdf`, { responseType: 'blob' }),
};

// Resignation API
export const resignationAPI = {
  apply: (data) => api.post('/resignation/apply', data),
  getMy: () => api.get('/resignation/my'),
  getAll: (params) => api.get('/resignation/all', { params }),
  approve: (id, data) => api.put(`/resignation/${id}/approve`, data),
  reject: (id, data) => api.put(`/resignation/${id}/reject`, data),
};

export const orgChartAPI = {
  getData: () => api.get('/org-chart'),
};

export const searchAPI = {
  global: (q) => api.get('/search/global', { params: { q } }),
};

export const stationeryAPI = {
  listTemplates: (type) => api.get('/stationery/templates', { params: { type } }),
  getTemplate: (id) => api.get(`/stationery/templates/${id}`),
  createTemplate: (data) => api.post('/stationery/templates', data),
  updateTemplate: (id, data) => api.put(`/stationery/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/stationery/templates/${id}`),
};

export const lettersReportsAPI = {
  downloadAddressProofLetter: (employeeId) => api.get(`/letters-reports/letters/address-proof/${employeeId}`, { responseType: 'blob' }),
  downloadConfirmationLetter: (employeeId, confirmationDate) => api.get(`/letters-reports/letters/confirmation/${employeeId}`, { params: { confirmation_date: confirmationDate }, responseType: 'blob' }),
  downloadEmploymentLetter: (employeeId) => api.get(`/letters-reports/letters/employment/${employeeId}`, { responseType: 'blob' }),
  downloadIncrementLetter: (employeeId) => api.get(`/letters-reports/letters/increment/${employeeId}`, { responseType: 'blob' }),
  downloadRelievingLetter: (employeeId, relievingDate) => api.get(`/letters-reports/letters/relieving/${employeeId}`, { params: { relieving_date: relievingDate }, responseType: 'blob' }),
  downloadAttendanceReport: (month, year) => api.get('/letters-reports/reports/attendance', { params: { month, year }, responseType: 'blob' }),
  downloadJoiningExitReport: () => api.get('/letters-reports/reports/joining-exit', { responseType: 'blob' }),
  downloadLeaveReport: (month, year) => api.get('/letters-reports/reports/leave', { params: { month, year }, responseType: 'blob' }),
  downloadPayrollReport: (month, year) => api.get('/letters-reports/reports/payroll', { params: { month, year }, responseType: 'blob' }),
  downloadPfEsiReport: (month, year) => api.get('/letters-reports/reports/pf-esi', { params: { month, year }, responseType: 'blob' }),
  downloadTdsReport: (month, year) => api.get('/letters-reports/reports/tds', { params: { month, year }, responseType: 'blob' }),
  downloadDeptPayrollCostReport: (month, year) => api.get('/letters-reports/reports/dept-payroll-cost', { params: { month, year }, responseType: 'blob' }),
  downloadSalaryStructureReport: () => api.get('/letters-reports/reports/salary-structure', { responseType: 'blob' }),
  downloadLateArrivalsReport: (month, year) => api.get('/letters-reports/reports/late-arrivals', { params: { month, year }, responseType: 'blob' }),
  downloadAbsenteeismReport: (month, year) => api.get('/letters-reports/reports/absenteeism', { params: { month, year }, responseType: 'blob' }),
  downloadLeaveBalanceReport: (year) => api.get('/letters-reports/reports/leave-balance', { params: { year }, responseType: 'blob' }),
  downloadLeaveHistoryReport: (month, year) => api.get('/letters-reports/reports/leave-history', { params: { month, year }, responseType: 'blob' }),
  downloadHeadcountReport: () => api.get('/letters-reports/reports/headcount', { responseType: 'blob' }),
};

export const kioskAPI = {
  enrollAll: () => api.post('/kiosk/enroll-all'),
  resetAttendance: () => api.delete('/kiosk/reset-attendance'),
};

export default api;
